import React, { useState, useEffect } from 'react';
import { KlasseFachEintrag } from '../../types';
import { useEindeutigeKlassen } from '../../lib/hooks';
import { nimmKlassenAktionZiel, KlassenZiel, oeffneKonferenzliste, oeffneAvSv } from '../../lib/nav';
import { KlassenKopf, KlassenReiter, KlassenUnter, BEREICH_VON } from '../klasse/KlassenKopf';
import { KlasseEinrichtenModal } from '../klassen-verwaltung';

// Welle U2: Sprung von außen auf Klasse › Klasse › Aktionen (aktionId = hervorheben)
export type AktionenZiel = { aktionId: number | null };
export type SchuelerAnsichtArt = 'uebersicht' | 'noten' | 'fehlzeiten';
export type UnterrichtArt = 'klassenbuch' | 'notizen';
export type KlasseArt = 'elternbeirat' | 'aktionen' | 'ausgaben';

// Welle K1: EIN Klassenkopf (Titel mit Klassenwahl, eine Zeile Kontext, ⋯-Menü) und höchstens zwei
// Reiter-Leisten. Die drei Bereiche bleiben eingehängt (Profil, Eingaben bleiben beim Wechsel erhalten);
// sichtbar ist nur der aktive. Ein offenes Profil (onDetail) blendet Kopf und Reiter aus.
export function KlasseTab({
  schueler,
  unterricht,
  klasse,
}: {
  schueler: (aktiveKlasse: KlasseFachEintrag | null, ansicht: SchuelerAnsichtArt, onDetail: (offen: boolean) => void) => React.ReactNode;
  unterricht: (aktiveKlasse: KlasseFachEintrag | null, art: UnterrichtArt, aktiv: boolean) => React.ReactNode;
  klasse: (aktiveKlasse: KlasseFachEintrag | null, aktionenZiel: AktionenZiel | null, art: KlasseArt) => React.ReactNode;
}) {
  const [unter, setUnter] = useState<KlassenUnter>('uebersicht');
  const [detailOffen, setDetailOffen] = useState(false);
  const [einrichten, setEinrichten] = useState<'manuell' | 'upload' | null>(null);
  const uniqueKlassen = useEindeutigeKlassen();

  const [aktiveKlasseLokal, setAktiveKlasseLokal] = useState<KlasseFachEintrag | null>(null);

  // Initialise / keep valid when meineKlassen loads or changes
  useEffect(() => {
    if (uniqueKlassen.length === 0) { setAktiveKlasseLokal(null); return; }
    if (!aktiveKlasseLokal || !uniqueKlassen.find(k => Number(k.klasse_id) === Number(aktiveKlasseLokal.klasse_id))) {
      setAktiveKlasseLokal(uniqueKlassen[0]);
    }
  }, [uniqueKlassen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Welle U2: Ziel (Klasse + Aktion) beim Einhängen abholen; die Klasse wird
  // gesetzt, sobald die Klassenliste sie enthält.
  const [ziel, setZiel] = useState<KlassenZiel | null>(null);
  useEffect(() => {
    const z = nimmKlassenAktionZiel();
    if (z) { setZiel(z); setUnter(z.tab === 'klasse' ? 'aktionen' : 'uebersicht'); }
  }, []);
  useEffect(() => {
    if (ziel?.klasseId == null) return;
    const k = uniqueKlassen.find(k => Number(k.klasse_id) === Number(ziel.klasseId));
    if (k) setAktiveKlasseLokal(k);
  }, [ziel, uniqueKlassen]);
  const aktionenZiel = React.useMemo<AktionenZiel | null>(() => ziel?.tab === 'klasse' ? { aktionId: ziel.aktionId } : null, [ziel]);

  const bereich = BEREICH_VON[unter];
  const schuelerArt: SchuelerAnsichtArt = bereich === 'schueler' ? unter as SchuelerAnsichtArt : 'uebersicht';
  const [unterrichtArt, setUnterrichtArt] = useState<UnterrichtArt>('klassenbuch');
  const [klasseArt, setKlasseArt] = useState<KlasseArt>('elternbeirat');
  useEffect(() => {
    if (bereich === 'unterricht') setUnterrichtArt(unter as UnterrichtArt);
    if (bereich === 'klasse') setKlasseArt(unter as KlasseArt);
  }, [unter, bereich]);

  return (
    <div className="ld-page-enter">
      {!detailOffen && (
        <>
          <KlassenKopf
            klassen={uniqueKlassen}
            aktiveKlasse={aktiveKlasseLokal}
            onWechsel={(k) => { setAktiveKlasseLokal(k); setZiel(null); }}
            menu={[
              { label: 'Schüler/in hinzufügen', icon: 'person_add', onClick: () => setEinrichten('manuell') },
              { label: 'Klassenliste aus Foto oder Datei', icon: 'upload_file', onClick: () => setEinrichten('upload') },
              // Welle B4: Konferenzliste (auch in der Notenansicht) und AV/SV-Vorschläge (auch im Konferenzlisten-Fenster)
              ...(aktiveKlasseLokal ? [
                { label: 'Konferenzliste', icon: 'groups', onClick: () => oeffneKonferenzliste({ klasseId: Number(aktiveKlasseLokal.klasse_id), klasseName: aktiveKlasseLokal.klasse_name }) },
                { label: 'AV/SV-Vorschläge', icon: 'rate_review', onClick: () => oeffneAvSv({ klasseId: Number(aktiveKlasseLokal.klasse_id), klasseName: aktiveKlasseLokal.klasse_name }) },
              ] : []),
            ]}
          />
          <KlassenReiter unter={unter} onUnter={setUnter} />
        </>
      )}

      <div style={{ display: bereich === 'schueler'   ? 'block' : 'none' }}>{schueler(aktiveKlasseLokal, schuelerArt, setDetailOffen)}</div>
      <div style={{ display: bereich === 'unterricht' ? 'block' : 'none' }}>{unterricht(aktiveKlasseLokal, unterrichtArt, bereich === 'unterricht')}</div>
      <div style={{ display: bereich === 'klasse'     ? 'block' : 'none' }}>{klasse(aktiveKlasseLokal, aktionenZiel, klasseArt)}</div>

      {einrichten && <KlasseEinrichtenModal defaultTab={einrichten} onClose={() => setEinrichten(null)} />}
    </div>
  );
}
